import { useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { colors } from '../../theme';
import {
    rotateDeviceKey, revokeDeviceKey, describeKeyActionOutcome,
    type KeyActionOutcome, type RotatedKey,
} from './deviceKeyActions';

/**
 * Rotate and revoke a device's API key from the console (#1542).
 *
 * Both actions change a credential a node is currently running on, so neither fires from a bare
 * click: rotate confirms, and revoke additionally requires a reason, which is what lands in the
 * device_api_key_audit row.
 */
export const DeviceKeyActionButtons = ({
    nodeId,
    keyId,
    disabled,
    onChanged,
}: {
    nodeId: string;
    keyId: number;
    /** True for a key that is already revoked or expired: there is nothing to act on. */
    disabled?: boolean;
    onChanged: () => void;
}) => {
    const [rotateOpen, setRotateOpen] = useState(false);
    const [revokeOpen, setRevokeOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState('');
    const [rotated, setRotated] = useState<RotatedKey | null>(null);
    const [copied, setCopied] = useState(false);

    const settle = (outcome: KeyActionOutcome, onSuccess: () => void) => {
        if (outcome.kind === 'rotated' || outcome.kind === 'revoked') {
            setProblem('');
            onSuccess();
            onChanged();
            return;
        }
        setProblem(describeKeyActionOutcome(outcome));
    };

    const doRotate = async () => {
        setBusy(true);
        try {
            const outcome = await rotateDeviceKey(nodeId);
            settle(outcome, () => {
                if (outcome.kind === 'rotated') setRotated(outcome.value);
            });
        } catch {
            setProblem('The request could not be sent.');
        } finally {
            setBusy(false);
        }
    };

    const doRevoke = async () => {
        setBusy(true);
        try {
            settle(await revokeDeviceKey(nodeId, keyId, reason), () => {
                setRevokeOpen(false);
                setReason('');
            });
        } catch {
            setProblem('The request could not be sent.');
        } finally {
            setBusy(false);
        }
    };

    const closeRotate = () => {
        // Closing is the only way out once the key is shown, and the key is gone after this.
        setRotateOpen(false);
        setRotated(null);
        setCopied(false);
        setProblem('');
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={disabled ? 'This key is no longer active' : 'Rotate key'}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Rotate key"
                            disabled={disabled}
                            onClick={() => setRotateOpen(true)}
                        >
                            <AutorenewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={disabled ? 'This key is no longer active' : 'Revoke key'}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Revoke key"
                            disabled={disabled}
                            onClick={() => setRevokeOpen(true)}
                        >
                            <BlockIcon sx={{ fontSize: 18, color: disabled ? undefined : colors.error }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Dialog open={rotateOpen} onClose={closeRotate} maxWidth="sm" fullWidth>
                <DialogTitle>Rotate the key for {nodeId}</DialogTitle>
                <DialogContent>
                    {rotated ? (
                        <Box>
                            <Typography sx={{ mb: 1 }}>
                                Copy this now. It is shown once and cannot be retrieved again.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <code
                                    data-testid="rotated-key-value"
                                    style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        background: colors.slate[100],
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        flex: 1,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {rotated.apiKey}
                                </code>
                                <Tooltip title={copied ? 'Copied' : 'Copy'}>
                                    <IconButton
                                        aria-label="Copy new key"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(rotated.apiKey);
                                                setCopied(true);
                                            } catch {
                                                setCopied(false);
                                            }
                                        }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ) : (
                        <Typography>
                            The node's current key is revoked and a new one is issued. The node keeps
                            working for the 24 hour rotation grace, then must pick up the new key.
                        </Typography>
                    )}
                    {problem && (
                        <Typography role="alert" sx={{ mt: 2, color: colors.error, bgcolor: alpha(colors.error, 0.08), p: 1.5, borderRadius: '8px' }}>
                            {problem}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRotate}>{rotated ? 'Done' : 'Cancel'}</Button>
                    {!rotated && (
                        <Button variant="contained" onClick={doRotate} disabled={busy}>
                            {busy ? 'Rotating' : 'Rotate key'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Revoke key {keyId} for {nodeId}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        This key stops working immediately. There is no grace period and the node
                        cannot exchange it for a new one, so a node running on it loses access until
                        it is re-credentialled.
                    </Typography>
                    <TextField
                        label="Reason"
                        fullWidth
                        required
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        helperText="Recorded against this key in the audit."
                    />
                    {problem && (
                        <Typography role="alert" sx={{ mt: 2, color: colors.error, bgcolor: alpha(colors.error, 0.08), p: 1.5, borderRadius: '8px' }}>
                            {problem}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={doRevoke}
                        // A reason is required by the route and lands in the audit row. Blocking
                        // here keeps the operator from a 400 that says the same thing later.
                        disabled={busy || reason.trim().length === 0}
                    >
                        {busy ? 'Revoking' : 'Revoke key'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
